import { useCallback, useEffect, useMemo, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { checkIsTableRowExpanded } from '../../utils/agGridUtils';
import { CapacityType } from '../../enums/CapacityType';
import { useAlertMessage } from '../../hooks';
import { parseStringToFloat } from '../../utils/numberUtils';
import CapacityHorizontalScrollTable from './CapacityHorizontalScrollTable';
import { getMonthColumns } from './helpers/utils';

const CapacityReportTable = forwardRef(
    (
        {
            firstMonth,
            onFirstMonthChanged,
            tableRows,
            saveCapacityReport,
            expandedRows,
            onExpandedRowsChanged,
            getExpandedRowsInTable,
            canWrite = true,
        },
        ref
    ) => {
        const { t } = useTranslation('allDeltas');
        const { dispatchAlert } = useAlertMessage();

        // Memoized so the props handed to the (memoized) CapacityHorizontalScrollTable
        // keep stable references. calculateSum in particular feeds createColumnDefs,
        // so an unstable reference forced a full column rebuild on every render.
        const calculateSum = useCallback((params) => {
            let sum = 0;
            if (params.rowNode.allLeafChildren.length > 0) {
                for (let i = 0; i < params.rowNode.allLeafChildren.length; i++) {
                    const child = params.rowNode.allLeafChildren[i];
                    if (child.data.capacityType.name === CapacityType.CUSTOMER_WORKLOAD.name) {
                        params.values[i] = child.data[params.column.colId]?.value || 0;
                    } else {
                        params.values[i] = 0;
                    }
                }
            }

            params.values.forEach((value) => (sum += value));

            return sum;
        }, []);

        const processCellForClipboard = useCallback((params) => {
            return params?.value ? params.value.toString().replace('.', ',') : null;
        }, []);

        const handleCellValueChange = useCallback((params) => {
            params.data[params.column.colId].changed = true;
        }, []);

        const handleCellEdit = useCallback(
            (params) => {
                if (!canWrite) return;
                // Skip if value hasn't changed (user just exited edit mode without changes)
                if (params.oldValue === params.newValue) {
                    return;
                }

                const enteredValue = parseStringToFloat(params.newValue);
                if (Number.isFinite(enteredValue)) {
                    saveCapacityReport({
                        cellParams: params,
                    });
                } else {
                    dispatchAlert({
                        message: t('notification.invalidNumber'),
                        type: 'error',
                    });
                }
            },
            [canWrite, saveCapacityReport, dispatchAlert, t]
        );

        const onPasteEnd = useCallback(() => {
            if (!canWrite) return;
            saveCapacityReport();
        }, [canWrite, saveCapacityReport]);

        const defaultColDef = useMemo(
            () => ({
                editable: canWrite,
                filter: true,
                sortable: true,
            }),
            [canWrite]
        );

        useEffect(() => {
            if (tableRows?.length && expandedRows.length) {
                ref.current.api.forEachNode((node) => {
                    const isTableRowExpanded = expandedRows.find((n) => checkIsTableRowExpanded(n, node));
                    if (isTableRowExpanded) {
                        node.setExpanded(true);
                    }
                });
            }
        }, [tableRows, expandedRows, ref]);

        return (
            <CapacityHorizontalScrollTable
                ref={ref}
                firstMonth={firstMonth}
                onFirstMonthChanged={onFirstMonthChanged}
                tableRows={tableRows}
                expandedRows={expandedRows}
                onExpandedRowsChanged={onExpandedRowsChanged}
                getExpandedRowsInTable={getExpandedRowsInTable}
                enableRangeSelection={true}
                copyHeadersToClipboard={true}
                suppressAggFuncInHeader={true}
                onCellEditingStopped={handleCellEdit}
                onCellValueChanged={handleCellValueChange}
                onPasteEnd={onPasteEnd}
                processCellForClipboard={processCellForClipboard}
                tableType="CAPACITY_REPORT"
                defaultColDef={defaultColDef}
                calculateSum={calculateSum}
                getMonthColumns={getMonthColumns}
            ></CapacityHorizontalScrollTable>
        );
    }
);

export default CapacityReportTable;
